import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  HardHat, ArrowLeft, ArrowRight, MapPin, Upload, CheckCircle,
  FileText, Shield, Camera, X, AlertCircle, Building
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { slug: 'roofing', name: 'Roofing' },
  { slug: 'remodeling', name: 'Remodeling' },
  { slug: 'hvac', name: 'HVAC' },
  { slug: 'solar', name: 'Solar' },
  { slug: 'plumbing', name: 'Plumbing' },
  { slug: 'electrical', name: 'Electrical' },
  { slug: 'windows', name: 'Windows & Doors' },
  { slug: 'concrete', name: 'Concrete' },
  { slug: 'flooring', name: 'Flooring' },
  { slug: 'painting', name: 'Painting' },
  { slug: 'landscaping', name: 'Landscaping' },
  { slug: 'fencing', name: 'Fencing' },
  { slug: 'handyperson', name: 'Handyperson' },
  { slug: 'cleaning', name: 'Cleaning' },
  { slug: 'deck_porch', name: 'Deck & Porch' },
  { slug: 'garage_doors', name: 'Garage Doors' },
  { slug: 'siding', name: 'Siding' },
  { slug: 'insulation', name: 'Insulation' },
  { slug: 'smart_home', name: 'Smart Home' },
];

const ContractorSetup = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingProfile, setExistingProfile] = useState(null);
  
  const [formData, setFormData] = useState({
    business_name: '',
    bio: '',
    categories: [],
    service_zip: '',
    service_radius_miles: 25,
    years_in_business: '',
    license_number: ''
  });
  
  const [licenseFile, setLicenseFile] = useState(null);
  const [insuranceFile, setInsuranceFile] = useState(null);
  const [uploadingLicense, setUploadingLicense] = useState(false);
  const [uploadingInsurance, setUploadingInsurance] = useState(false);
  const [licenseUploaded, setLicenseUploaded] = useState(false);
  const [insuranceUploaded, setInsuranceUploaded] = useState(false);

  useEffect(() => {
    checkExistingProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkExistingProfile = async () => {
    try {
      const response = await axios.get(`${API}/contractors/profile`);
      if (response.data.profile) {
        setExistingProfile(response.data.profile);
        setFormData({
          business_name: response.data.profile.business_name || '',
          bio: response.data.profile.bio || '',
          categories: response.data.profile.categories || [],
          service_zip: response.data.profile.service_zip || '',
          service_radius_miles: response.data.profile.service_radius_miles || 25,
          years_in_business: response.data.profile.years_in_business || '',
          license_number: response.data.profile.license_number || ''
        });
        if (response.data.profile.license_document_url) {
          setLicenseUploaded(true);
        }
        if (response.data.profile.insurance_document_url) {
          setInsuranceUploaded(true);
        }
      }
    } catch (error) {
      // Profile doesn't exist yet — pre-fill from signup info on User
      if (user) {
        setFormData(prev => ({
          ...prev,
          service_zip: user.zip_code || prev.service_zip,
          license_number: user.license_number || prev.license_number,
        }));
      }
    }
  };

  const handleCategoryToggle = (slug) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(slug)
        ? prev.categories.filter(c => c !== slug)
        : [...prev.categories, slug]
    }));
  };

  const handleCreateProfile = async () => {
    setError('');
    setLoading(true);

    try {
      if (existingProfile) {
        await axios.put(`${API}/contractors/profile`, formData);
      } else {
        await axios.post(`${API}/contractors/profile`, formData);
      }
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLicenseUpload = async () => {
    if (!licenseFile) return;
    
    setUploadingLicense(true);
    setError('');
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', licenseFile);
    
    try {
      await axios.post(`${API}/contractors/upload-license`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setLicenseUploaded(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'License upload failed');
    } finally {
      setUploadingLicense(false);
    }
  };

  const handleInsuranceUpload = async () => {
    if (!insuranceFile) return;
    
    setUploadingInsurance(true);
    setError('');
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', insuranceFile);
    
    try {
      await axios.post(`${API}/contractors/upload-insurance`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setInsuranceUploaded(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Insurance upload failed');
    } finally {
      setUploadingInsurance(false);
    }
  };

  const canProceedStep1 = formData.business_name && formData.categories.length > 0;
  const canProceedStep2 = formData.service_zip && formData.service_radius_miles;
  const canFinish = licenseUploaded && insuranceUploaded;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/contractor/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Back to Dashboard</span>
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-600 rounded-sm flex items-center justify-center">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-black text-white tracking-tight">PROFILE SETUP</span>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex-1 flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  s <= step ? 'bg-orange-600 text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                  {s < step ? <CheckCircle className="w-5 h-5" /> : s}
                </div>
                {s < 4 && (
                  <div className={`flex-1 h-1 mx-2 ${s < step ? 'bg-orange-600' : 'bg-slate-200'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-slate-500">
            <span>Business Info</span>
            <span>Service Area</span>
            <span>Documents</span>
            <span>Complete</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6 text-sm flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Step 1: Business Info */}
        {step === 1 && (
          <div>
            <h2 className="text-2xl font-black text-slate-900 mb-2">Tell us about your business</h2>
            <p className="text-slate-600 mb-6">This information will be shown to potential clients.</p>
            
            <div className="space-y-6">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  Business Name *
                </label>
                <input
                  type="text"
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  placeholder="e.g., Smith Roofing Co."
                  className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                  data-testid="business-name-input"
                />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  Bio / Description
                </label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Tell clients about your experience, specialties, and what makes you stand out..."
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                  data-testid="bio-input"
                />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  Years in Business
                </label>
                <input
                  type="number"
                  value={formData.years_in_business}
                  onChange={(e) => setFormData({ ...formData, years_in_business: parseInt(e.target.value) || '' })}
                  placeholder="e.g., 10"
                  min="0"
                  className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                  data-testid="years-input"
                />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  Service Categories * (Select all that apply)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.slug}
                      type="button"
                      onClick={() => handleCategoryToggle(cat.slug)}
                      className={`p-3 rounded-sm border-2 text-left text-sm transition-all ${
                        formData.categories.includes(cat.slug)
                          ? 'border-orange-600 bg-orange-50 text-orange-700'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`category-${cat.slug}`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
                <p className="text-sm text-slate-500 mt-2">
                  Selected: {formData.categories.length} categories
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Service Area */}
        {step === 2 && (
          <div>
            <h2 className="text-2xl font-black text-slate-900 mb-2">Define your service area</h2>
            <p className="text-slate-600 mb-6">You'll only receive leads within this area.</p>
            
            <div className="space-y-6">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Your Zip Code *
                </label>
                <input
                  type="text"
                  value={formData.service_zip}
                  onChange={(e) => setFormData({ ...formData, service_zip: e.target.value })}
                  placeholder="e.g., 90210"
                  maxLength={5}
                  className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                  data-testid="zip-code-input"
                />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  Service Radius: {formData.service_radius_miles} miles
                </label>
                <input
                  type="range"
                  min="5"
                  max="100"
                  step="5"
                  value={formData.service_radius_miles}
                  onChange={(e) => setFormData({ ...formData, service_radius_miles: parseInt(e.target.value) })}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-orange-600"
                  data-testid="radius-slider"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>5 mi</span>
                  <span>50 mi</span>
                  <span>100 mi</span>
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  License Number (Optional)
                </label>
                <input
                  type="text"
                  value={formData.license_number}
                  onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                  placeholder="e.g., CA-123456"
                  className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                  data-testid="license-number-input"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Documents */}
        {step === 3 && (
          <div>
            <h2 className="text-2xl font-black text-slate-900 mb-2">Upload verification documents</h2>
            <p className="text-slate-600 mb-6">
              License and insurance are required to receive leads. Documents will be reviewed within 24-48 hours.
            </p>
            
            <div className="space-y-6">
              {/* License Upload */}
              <div className="bg-white border border-slate-200 rounded-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      licenseUploaded ? 'bg-green-100' : 'bg-slate-100'
                    }`}>
                      {licenseUploaded ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <FileText className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">Contractor License *</h3>
                      <p className="text-sm text-slate-500">PDF, JPEG, or PNG - Max 10MB</p>
                    </div>
                  </div>
                  {licenseUploaded && (
                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded text-sm font-bold">
                      Uploaded
                    </span>
                  )}
                </div>
                
                {!licenseUploaded && (
                  <div className="flex items-center gap-4">
                    <label className="flex-1">
                      <div className="border-2 border-dashed border-slate-300 rounded-sm p-4 text-center cursor-pointer hover:border-orange-400 transition-colors">
                        <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                        <span className="text-sm text-slate-600">
                          {licenseFile ? licenseFile.name : 'Click to select file'}
                        </span>
                      </div>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => setLicenseFile(e.target.files[0])}
                        className="hidden"
                        data-testid="license-file-input"
                      />
                    </label>
                    <button
                      onClick={handleLicenseUpload}
                      disabled={!licenseFile || uploadingLicense}
                      className="bg-orange-600 text-white px-4 py-3 rounded-sm font-bold disabled:opacity-50"
                      data-testid="upload-license-btn"
                    >
                      {uploadingLicense ? 'Uploading...' : 'Upload'}
                    </button>
                  </div>
                )}
              </div>
              
              {/* Insurance Upload */}
              <div className="bg-white border border-slate-200 rounded-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      insuranceUploaded ? 'bg-green-100' : 'bg-slate-100'
                    }`}>
                      {insuranceUploaded ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <Shield className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">Insurance Certificate *</h3>
                      <p className="text-sm text-slate-500">PDF, JPEG, or PNG - Max 10MB</p>
                    </div>
                  </div>
                  {insuranceUploaded && (
                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded text-sm font-bold">
                      Uploaded
                    </span>
                  )}
                </div>
                
                {!insuranceUploaded && (
                  <div className="flex items-center gap-4">
                    <label className="flex-1">
                      <div className="border-2 border-dashed border-slate-300 rounded-sm p-4 text-center cursor-pointer hover:border-orange-400 transition-colors">
                        <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                        <span className="text-sm text-slate-600">
                          {insuranceFile ? insuranceFile.name : 'Click to select file'}
                        </span>
                      </div>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => setInsuranceFile(e.target.files[0])}
                        className="hidden"
                        data-testid="insurance-file-input"
                      />
                    </label>
                    <button
                      onClick={handleInsuranceUpload}
                      disabled={!insuranceFile || uploadingInsurance}
                      className="bg-orange-600 text-white px-4 py-3 rounded-sm font-bold disabled:opacity-50"
                      data-testid="upload-insurance-btn"
                    >
                      {uploadingInsurance ? 'Uploading...' : 'Upload'}
                    </button>
                  </div>
                )}
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Your documents will be reviewed by our team within 24-48 hours. 
                  Once approved, your profile will be activated and you'll start receiving leads.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Complete */}
        {step === 4 && (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-black text-slate-900 mb-4">Setup Complete!</h2>
            <p className="text-slate-600 mb-8 max-w-md mx-auto">
              Your documents are being reviewed. You'll receive an email once your profile is approved.
              In the meantime, make sure to top up your wallet to receive leads.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contractor/wallet"
                className="bg-orange-600 text-white px-8 py-3 rounded-sm font-bold uppercase tracking-wide"
                data-testid="go-to-wallet-btn"
              >
                Top Up Wallet
              </Link>
              <Link
                to="/contractor/dashboard"
                className="bg-slate-200 text-slate-700 px-8 py-3 rounded-sm font-bold uppercase tracking-wide"
              >
                Go to Dashboard
              </Link>
            </div>
          </div>
        )}

        {/* Navigation */}
        {step < 4 && (
          <div className="flex items-center justify-between mt-8">
            {step > 1 ? (
              <button
                onClick={() => setStep(step - 1)}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium"
              >
                <ArrowLeft className="w-5 h-5" /> Back
              </button>
            ) : (
              <div />
            )}
            
            {step === 1 && (
              <button
                onClick={() => setStep(2)}
                disabled={!canProceedStep1}
                className="bg-orange-600 hover:bg-orange-700 text-white font-bold uppercase tracking-wide px-6 py-3 rounded-sm transition-colors flex items-center gap-2 disabled:opacity-50"
                data-testid="next-step-btn"
              >
                Continue <ArrowRight className="w-5 h-5" />
              </button>
            )}
            
            {step === 2 && (
              <button
                onClick={handleCreateProfile}
                disabled={!canProceedStep2 || loading}
                className="bg-orange-600 hover:bg-orange-700 text-white font-bold uppercase tracking-wide px-6 py-3 rounded-sm transition-colors flex items-center gap-2 disabled:opacity-50"
                data-testid="save-profile-btn"
              >
                {loading ? 'Saving...' : 'Save & Continue'} <ArrowRight className="w-5 h-5" />
              </button>
            )}
            
            {step === 3 && (
              <button
                onClick={() => setStep(4)}
                disabled={!canFinish}
                className="bg-orange-600 hover:bg-orange-700 text-white font-bold uppercase tracking-wide px-6 py-3 rounded-sm transition-colors flex items-center gap-2 disabled:opacity-50"
                data-testid="complete-setup-btn"
              >
                Complete Setup <ArrowRight className="w-5 h-5" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractorSetup;
